import React from 'react';
import { colors, spacing, borderRadius } from '../../styles/designTokens';

/**
 * FormField Component
 * 
 * Standardized form field with label, input, error, and help text.
 * Ensures consistent form styling throughout the application.
 * 
 * Usage:
 * <FormField
 *   label="Email Address"
 *   name="email"
 *   type="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   error={errors.email}
 *   helpText="We'll never share your email"
 *   required
 * />
 * 
 * Props:
 * - label: Field label (required)
 * - name: Input name attribute
 * - type: Input type (default: 'text')
 * - value: Input value
 * - onChange: Change handler
 * - error: Error message to display
 * - helpText: Helper text below input
 * - required: Boolean - shows required indicator
 * - disabled: Boolean - disables input
 * - placeholder: Input placeholder
 * - className: Additional CSS classes
 */

const FormField = ({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error,
  helpText,
  required = false,
  disabled = false,
  placeholder,
  className = '',
  ...props
}) => {
  const inputId = `form-field-${name}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;

  const inputStyles = {
    width: '100%',
    padding: `${spacing.sm} ${spacing.md}`,
    border: `1px solid ${error ? colors.error.main : colors.neutral[300]}`,
    borderRadius: borderRadius.md,
    fontSize: '0.875rem',
    transition: 'border-color 0.2s ease',
    backgroundColor: disabled ? colors.neutral[100] : '#ffffff',
    color: disabled ? colors.neutral[500] : colors.neutral[900]
  };

  const labelStyles = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.neutral[700],
    marginBottom: spacing.xs
  };

  const errorStyles = {
    fontSize: '0.75rem',
    color: colors.error.main,
    marginTop: spacing.xs
  };

  const helpStyles = {
    fontSize: '0.75rem',
    color: colors.neutral[500],
    marginTop: spacing.xs
  };

  return (
    <div className={className}>
      {/* Label */}
      {label && (
        <label htmlFor={inputId} style={labelStyles}>
          {label}
          {required && <span style={{ color: colors.error.main, marginLeft: spacing.xs }}>*</span>}
        </label>
      )}

      {/* Input */}
      <input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={`${error ? errorId : ''} ${helpText ? helpId : ''}`.trim()}
        style={inputStyles}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        {...props}
      />

      {/* Error Message */}
      {error && (
        <div id={errorId} role="alert" style={errorStyles}>
          ⚠ {error}
        </div>
      )}

      {/* Help Text */}
      {helpText && !error && (
        <div id={helpId} style={helpStyles}>
          {helpText}
        </div>
      )}
    </div>
  );
};

/**
 * FormFieldTextarea Component
 * 
 * Textarea variant of FormField
 */
export const FormFieldTextarea = ({
  label,
  name,
  value,
  onChange,
  error,
  helpText,
  required = false,
  disabled = false,
  placeholder,
  rows = 4,
  className = '',
  ...props
}) => {
  const textareaId = `form-field-${name}`;
  const errorId = `${textareaId}-error`;
  const helpId = `${textareaId}-help`;

  const textareaStyles = {
    width: '100%',
    padding: `${spacing.sm} ${spacing.md}`,
    border: `1px solid ${error ? colors.error.main : colors.neutral[300]}`,
    borderRadius: borderRadius.md,
    fontSize: '0.875rem',
    transition: 'border-color 0.2s ease',
    backgroundColor: disabled ? colors.neutral[100] : '#ffffff',
    color: disabled ? colors.neutral[500] : colors.neutral[900],
    resize: 'vertical'
  };

  const labelStyles = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.neutral[700],
    marginBottom: spacing.xs
  };

  const errorStyles = {
    fontSize: '0.75rem',
    color: colors.error.main,
    marginTop: spacing.xs
  };

  const helpStyles = {
    fontSize: '0.75rem',
    color: colors.neutral[500],
    marginTop: spacing.xs
  };

  return (
    <div className={className}>
      {/* Label */}
      {label && (
        <label htmlFor={textareaId} style={labelStyles}>
          {label}
          {required && <span style={{ color: colors.error.main, marginLeft: spacing.xs }}>*</span>}
        </label>
      )}

      {/* Textarea */}
      <textarea
        id={textareaId}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        rows={rows}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={`${error ? errorId : ''} ${helpText ? helpId : ''}`.trim()}
        style={textareaStyles}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        {...props}
      />

      {/* Error Message */}
      {error && (
        <div id={errorId} role="alert" style={errorStyles}>
          ⚠ {error}
        </div>
      )}

      {/* Help Text */}
      {helpText && !error && (
        <div id={helpId} style={helpStyles}>
          {helpText}
        </div>
      )}
    </div>
  );
};

/**
 * FormFieldSelect Component
 * 
 * Select dropdown variant of FormField
 */
export const FormFieldSelect = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error,
  helpText,
  required = false,
  disabled = false,
  placeholder = 'Select an option',
  className = '',
  ...props
}) => {
  const selectId = `form-field-${name}`;
  const errorId = `${selectId}-error`;
  const helpId = `${selectId}-help`;

  const selectStyles = {
    width: '100%',
    padding: `${spacing.sm} ${spacing.md}`,
    border: `1px solid ${error ? colors.error.main : colors.neutral[300]}`,
    borderRadius: borderRadius.md,
    fontSize: '0.875rem',
    transition: 'border-color 0.2s ease',
    backgroundColor: disabled ? colors.neutral[100] : '#ffffff',
    color: disabled ? colors.neutral[500] : colors.neutral[900]
  };

  const labelStyles = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.neutral[700],
    marginBottom: spacing.xs
  };

  const errorStyles = {
    fontSize: '0.75rem',
    color: colors.error.main,
    marginTop: spacing.xs
  };

  const helpStyles = {
    fontSize: '0.75rem',
    color: colors.neutral[500],
    marginTop: spacing.xs
  };

  return (
    <div className={className}>
      {/* Label */}
      {label && (
        <label htmlFor={selectId} style={labelStyles}>
          {label}
          {required && <span style={{ color: colors.error.main, marginLeft: spacing.xs }}>*</span>}
        </label>
      )}

      {/* Select */}
      <select
        id={selectId}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        required={required}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={`${error ? errorId : ''} ${helpText ? helpId : ''}`.trim()}
        style={selectStyles}
        className="focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option, index) => (
          <option key={option.value || index} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Error Message */}
      {error && (
        <div id={errorId} role="alert" style={errorStyles}>
          ⚠ {error}
        </div>
      )}

      {/* Help Text */}
      {helpText && !error && (
        <div id={helpId} style={helpStyles}>
          {helpText}
        </div>
      )}
    </div>
  );
};

export default FormField;

